(() => {
  const root = document.documentElement;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let ticking = false;
  let revealObserver;

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

  function revealElements(scope = document) {
    const elements = scope.querySelectorAll('[data-ebio-reveal]:not([data-ebio-ready])');

    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      elements.forEach((element) => {
        element.dataset.ebioReady = 'true';
        element.classList.add('is-visible');
      });
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
      );
    }

    elements.forEach((element) => {
      element.dataset.ebioReady = 'true';
      revealObserver.observe(element);
    });
  }

  function updateDepthScenes() {
    document.querySelectorAll('[data-ebio-depth-scene]').forEach((scene) => {
      const rect = scene.getBoundingClientRect();
      const travel = Math.max(scene.offsetHeight - window.innerHeight, 1);
      const progress = clamp(-rect.top / travel);
      scene.style.setProperty('--ebio-progress', progress.toFixed(4));
      scene.style.setProperty('--ebio-scene-scale', (1 + progress * 0.075).toFixed(4));
      scene.style.setProperty('--ebio-scene-lift', `${Math.round(progress * -72)}px`);
      scene.style.setProperty('--ebio-scene-opacity', Math.max(0.3, 1 - progress * 0.72).toFixed(3));
    });
  }

  function updateJourneys() {
    document.querySelectorAll('[data-ebio-journey]').forEach((journey) => {
      const viewport = journey.querySelector('[data-ebio-journey-viewport]');
      const track = journey.querySelector('[data-ebio-journey-track]');
      if (!viewport || !track) return;

      const rect = journey.getBoundingClientRect();
      const travel = Math.max(journey.offsetHeight - window.innerHeight, 1);
      const progress = clamp(-rect.top / travel);
      const maxX = Math.max(track.scrollWidth - viewport.clientWidth, 0);
      const x = Math.round(progress * maxX * -1);

      journey.style.setProperty('--ebio-journey-x', `${x}px`);
      journey.style.setProperty('--ebio-journey-progress', progress.toFixed(4));
      journey.style.setProperty('--ebio-journey-drift', `${Math.round(progress * 160)}px`);
      journey.style.setProperty('--ebio-journey-drift-back', `${Math.round(progress * -72)}px`);
      journey.style.setProperty('--ebio-journey-drift-soft', `${Math.round(progress * -38)}px`);
      journey.style.setProperty('--ebio-journey-orbit', `${Math.round(progress * 38)}deg`);
      journey.style.setProperty('--ebio-journey-light-x', `${Math.round(18 + progress * 64)}%`);

      const steps = track.querySelectorAll('[data-ebio-journey-step]');
      const activeIndex = Math.min(steps.length - 1, Math.floor(progress * steps.length));
      steps.forEach((step, index) => {
        step.classList.toggle('is-active', index === activeIndex);
        step.classList.toggle('is-past', index < activeIndex);
      });
    });
  }

  function initSliders(scope = document) {
    const sliders = scope.matches?.('[data-ebio-slider]')
      ? [scope]
      : scope.querySelectorAll('[data-ebio-slider]');

    sliders.forEach((slider) => {
      if (slider.dataset.ebioSliderReady === 'true') return;

      const track = slider.querySelector('[data-ebio-slider-track]');
      const previous = slider.querySelector('[data-ebio-slider-prev]');
      const next = slider.querySelector('[data-ebio-slider-next]');
      if (!track || !previous || !next) return;

      const getStep = () => {
        const firstCard = track.firstElementChild;
        const gap = parseFloat(window.getComputedStyle(track).columnGap || window.getComputedStyle(track).gap || 0);
        return firstCard ? firstCard.getBoundingClientRect().width + gap : track.clientWidth * 0.84;
      };

      const updateControls = () => {
        const maxScroll = Math.max(track.scrollWidth - track.clientWidth - 2, 0);
        previous.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= maxScroll;
      };

      previous.addEventListener('click', () => {
        track.scrollBy({ left: -getStep(), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      });

      next.addEventListener('click', () => {
        track.scrollBy({ left: getStep(), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      });

      track.addEventListener('scroll', updateControls, { passive: true });
      window.addEventListener('resize', updateControls, { passive: true });
      slider.dataset.ebioSliderReady = 'true';
      updateControls();
    });
  }

  function update() {
    ticking = false;
    if (reducedMotion.matches) return;
    updateDepthScenes();
    updateJourneys();
  }

  function requestUpdate() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  }

  function init(scope = document) {
    revealElements(scope);
    initSliders(scope);
    requestUpdate();
  }

  const scrollContainer = document.querySelector('.page-wrapper');
  window.addEventListener('scroll', requestUpdate, { passive: true });
  scrollContainer?.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate, { passive: true });
  reducedMotion.addEventListener?.('change', () => init(document));

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:reorder', requestUpdate);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  root.classList.add('ebio-motion-ready');
})();
